/**
 * @fileoverview Generic standardized Pendo tracking hook.
 * Provides a clean API for tracking standardized events across the application.
 */

import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { selectUser, selectEnvironment } from "../../store/selector";
import { trackPendoEvent } from "./pendoUtils";
import { trackMixpanelEvent } from "../mixpanel/mixPanelUtils";
import { getEnvironmentType } from "../environment";
import { trackEventsBackendApi } from "../../api/api";

// ============================================================================
// CONSTANTS
// ============================================================================

const PRODUCT = "financial_navigation";

export const EventNames = Object.freeze({
  PAGE_IMPRESSION: "page_impression",
  BOX_IMPRESSION: "box_impression",
  BOX_CLICK: "box_click",
  BUTTON_CLICK: "button_click",
  LINK_CLICK: "link_click",
  LINK_SENT: "link_sent",
  POP_UP_IMPRESSION: "pop_up_impression",
  BANNER_IMPRESSION: "banner_impression",
  BANNER_CLICK: "banner_click",
  FORM_STARTED: "form_started",
  FORM_SUBMITTED: "form_submitted",
  FORM_LEFT: "form_left",
  FORM_SAVE_DRAFT: "form_save_draft",
  SEND_LINK_SUCCESS: "send_link_success",
  SEND_LINK_CONFIRMED: "send_link_confirmed",
  SEND_LINK_FAILED: "send_link_failed",
  BULK_ACTION_VIEWED: "bulk_action_viewed",
  BULK_ACTION_TRIGGERED: "bulk_action_triggered",
  BULK_ACTION_COMPLETED: "bulk_action_completed",
  BULK_ACTION_REPORT_DOWNLOADED: "bulk_action_report_downloaded",
  INLINE_EDIT_VIEWED: "inline_edit_viewed",
  INLINE_EDIT_TRIGGERED: "inline_edit_triggered",
  INLINE_EDIT_COMPLETED: "inline_edit_completed"
});

export const ContentTypes = Object.freeze({
  APPLICATION: "Application",
  PATIENT: "Patient",
  FORM: "Form",
  LINK: "Link",
  BUTTON: "Button",
  CONSENT: "Consent"
});

export const LayoutTypes = Object.freeze({
  LIST: "list",
  CARD: "card",
  PANEL: "panel",
  MODAL: "modal",
  BUTTON: "button",
  TABLE: "Table",
  FORM: "Form"
});

export const FeatureGroups = Object.freeze({
  POTENTIAL_SAVINGS: "potential_savings",
  EXPRESS_ENROLLMENT: "express_enrollment",
  ENROLLMENTS: "enrollments",
  SELF_ENROLL: "self_enroll",
  DASHBOARD: "dashboard",
  EXPORT_ACTION: "export_action",
  PATIENT_LIST_MANAGEMENT: "patient_list_management",
  ALL_PATIENTS: "all_patients",
  SAVED_LISTS: "saved_lists",
  ECONSENT: "econsent",
  PATIENT_OVERVIEW: "patient_overview",
  BULK_UPDATE: "bulk_update",
  INLINE_UPDATE: "inline_update",
  REDI_PARTNERSHIP: "redi_partnership",
  CONTACT_SUPPORT: "contact_support"
});

// Entry points for the Contact Support modal's opened-event attribution.
export const SUPPORT_ENTRY_POINTS = Object.freeze({
  SIDEBAR: "sidebar",
  PATIENT_HEADER: "patient_header",
  CONSENT_ALERT: "consent_alert"
});

export const ExportActionStatus = Object.freeze({
  SUCCESS: "success",
  FAILURE: "failure"
});

export const ExportActionContentKeys = Object.freeze({
  COMPLETED: "export_action_completed",
  FAILURE: "export_action_failure"
});

export const PageKeys = Object.freeze({
  ALL_PATIENTS: "all_patients",
  PATIENT_OVERVIEW: "patient_overview",
  POTENTIAL_SAVINGS: "patient_potential_savings",
  SAVED_LISTS: "saved_lists",
  FILTER_LIST_VIEW: "filter_list_view",
  VISIT_SCHEDULE: "visit_schedule",
  HIGH_PRIORITY: "high_priority",
  ALL_ENROLLMENTS: "all_enrollments",
  PENDING_RENEWALS: "pending_renewals"
});

// Redi partnership content keys
export const RediContentKeys = Object.freeze({
  // FN Success Modal (pop_up_impression + button_click events)
  FN_SUCCESS_MESSAGE: "fn_success_message",
  FN_SUCCESS_GOT_IT: "fn_success_got_it",
  FN_SUCCESS_LEARN_MORE: "fn_success_learn_more",
  FN_SUCCESS_SETTINGS: "fn_success_settings",
  FN_SUCCESS_TIMEOUT: "fn_success_message_timeout",
  // FN Missing Contact Modal (pop_up_impression + button_click events)
  FN_MISSING_CONTACT: "fn_missing_contact",
  FN_MISSING_CONTACT_ADD: "fn_missing_contact_add",
  FN_MISSING_CONTACT_DISMISS: "fn_missing_contact_dismiss",
  // FN Program Page (box_impression event)
  FN_PROGRAM_PAGE: "fn_program_page",
  // FN Send to Patient Form (form_started + form_submitted events)
  SEND_TO_PATIENT: "send_to_patient"
});

// Redi partnership features
export const RediFeatures = Object.freeze({
  REDI_INVITATION: "redi_invitation",
  REDI_PROGRAM_DETAILS: "redi_program_details"
});

// Express Enroll specific content keys
export const EEContentKeys = Object.freeze({
  // All Patients Flow
  ALL_PATIENTS_PAGE: "all_patients_page",
  QUICK_EE_FILTER_BTN: "quick_ee_filter_btn",
  EE_FILTER_MENU_CHECKBOX: "ee_filter_menu_checkbox",
  PATIENT_ROW_ENROLL_BTN: "patient_row_enroll_btn",

  // Potential Savings Flow
  PREPARE_NEW_ENROLLMENT_BTN: "prepare_new_enrollment_btn",
  SEND_TO_PATIENT_BTN: "send_to_patient_btn",

  // Saved Lists Flow
  SAVED_LISTS_PAGE: "saved_lists_page",
  EE_SAVED_LIST_CLICK: "ee_saved_list_click",
  SAVE_AS_NEW_LIST_BTN: "save_as_new_list_btn",
  SAVED_LIST_CREATED: "saved_list_created",
  SAVED_LIST_UPDATED: "saved_list_updated"
});

export const SelfEnrollContentKeys = Object.freeze({
  SEND_TO_PATIENT_BTN: "send_to_patient_btn",
  SEND_LINK_TO_PATIENT_BTN: "self_enroll_link_send"
});

// Bulk Action specific content keys
export const BulkActionContentKeys = Object.freeze({
  BULK_ACTION_VIEWED: "bulk_action_viewed",
  BULK_ACTION_TRIGGERED: "bulk_action_triggered",
  BULK_ACTION_COMPLETED: "bulk_action_completed",
  BULK_ACTION_REPORT_DOWNLOADED: "bulk_action_report_downloaded"
});

export const InlineEditContentKeys = Object.freeze({
  INLINE_EDIT_VIEWED: "inline_edit_viewed",
  INLINE_EDIT_TRIGGERED: "inline_edit_triggered",
  INLINE_EDIT_COMPLETED: "inline_edit_completed"
});

export const InlineEditStatus = Object.freeze({
  SUCCESS: "success",
  FAILURE: "failure"
});

export const EE_ENROLLMENT_ORIGIN_KEY = "ee_enrollment_origin";

export const EEOriginValues = Object.freeze({
  ROW_ENROLL_BUTTON: "row_enroll_button",
  POTENTIAL_SAVINGS_PAGE: "potential_savings_page"
});

// ============================================================================
// UTILITY FUNCTIONS (exported for testing)
// ============================================================================

// Envelope fields sent top-level to the relay; everything else goes under `properties`
export const splitEnvelopeFields = (event) => {
  const { event_name, event_uuid, event_ts, session_id, page_uri, device_type, patient_id, ...properties } = event;
  return {
    event_name,
    event_uuid,
    event_ts,
    session_id,
    page_uri,
    device_type,
    patient_id,
    // patient_id stays in properties too — the Pendo adapter only forwards properties
    properties: { ...properties, ...(patient_id != null && { patient_id }) }
  };
};

export const generateEventUuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const SESSION_STORAGE_KEY = "pendo_session_id";

export const getSessionId = () => {
  try {
    let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

    if (!sessionId) {
      sessionId = generateEventUuid();
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }

    return sessionId;
  } catch (error) {
    // localStorage unavailable (e.g., private browsing mode) or quota exceeded
    // Fallback to Pendo visitor ID if available
    try {
      if (window.pendo?.getVisitorId) {
        return window.pendo.getVisitorId() || null;
      }
    } catch {
      // Pendo unavailable or getVisitorId throws
    }

    return null;
  }
};

export const getDeviceType = () => {
  try {
    const ua = navigator.userAgent.toLowerCase();
    if (/mobile|iphone|android.*mobile/i.test(ua)) return "Mobile";
    if (/tablet|ipad|android(?!.*mobile)/i.test(ua)) return "Tablet";
    return "Desktop";
  } catch {
    return "Desktop";
  }
};

// Fire-and-forget relay of a billable event to the backend; skips client SDKs to avoid double-counting
const relayEventToBackend = (event) => {
  trackEventsBackendApi([splitEnvelopeFields(event)]).catch((error) => {
    // eslint-disable-next-line no-console
    console.error("[Analytics] backend event relay failed", {
      eventName: event.event_name,
      status: error.response?.status,
      message: error.message
    });
  });
};

export const extractApFields = (potentialSaving, assistanceProgram) => {
  const result = {};

  if (potentialSaving?.name || assistanceProgram?.name) {
    result.ap_name = potentialSaving?.name || assistanceProgram?.name;
  }
  if (potentialSaving?.assistanceProgram || assistanceProgram?.id) {
    result.ap_id = potentialSaving?.assistanceProgram || assistanceProgram?.id;
  }
  if (potentialSaving?.id) {
    result.pap_id = potentialSaving.id;
  }
  if (potentialSaving?.currentApplication) {
    result.apa_id = potentialSaving.currentApplication;
  }

  const drugName =
    potentialSaving?.drugDisplayName ||
    potentialSaving?.approvedRxnormDrugs?.map((d) => d.tmDisplayName).join(", ") ||
    assistanceProgram?.drugName;
  if (drugName) result.drug_name = drugName;

  const drugId =
    potentialSaving?.matchedDrugCui || potentialSaving?.approvedRxnormDrugs?.map((d) => d.ttyCui).join(", ");
  if (drugId) result.drug_id = drugId;

  if (potentialSaving?.APProvider || assistanceProgram?.providedBy) {
    result.manufacturer_name = potentialSaving?.APProvider || assistanceProgram?.providedBy;
  }

  return result;
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Generic hook for tracking standardized Pendo events.
 * Automatically handles user, environment, patient context, and deduplication.
 *
 * @returns {Object} Tracking functions and utilities.
 *
 * @example
 * const { track, trackOnce } = useStandardizedTracking();
 *
 * // Track a simple event
 * track(EventNames.BUTTON_CLICK, {
 *   featureGroup: FeatureGroups.POTENTIAL_SAVINGS,
 *   contentKey: "prepare_button",
 *   potentialSaving,
 *   assistanceProgram
 * });
 *
 * // Track once (deduplicated)
 * trackOnce('page-impression', () => {
 *   track(EventNames.PAGE_IMPRESSION, { ... });
 * });
 */
export const useStandardizedTracking = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const site = useSelector(selectEnvironment);
  const { patientId } = useParams();
  const location = useLocation();

  // Refs for deduplication
  const trackedRef = useRef({});

  /**
   * Core tracking function - builds and dispatches a standardized event.
   *
   * @param {string} eventName - Event name from EventNames.
   * @param {Object} options - Event options.
   * @param {string} [options.featureGroup] - Feature group from FeatureGroups.
   * @param {string} [options.feature] - Specific feature identifier from FeatureGroups.
   * @param {string} [options.pageKey] - Logical page name.
   * @param {string} [options.contentType] - Content type from ContentTypes.
   * @param {string} [options.contentKey] - Content identifier.
   * @param {string} [options.layout] - Layout type from LayoutTypes.
   * @param {Object} [options.potentialSaving] - Potential saving object for AP fields.
   * @param {Object} [options.assistanceProgram] - Assistance program object for AP fields.
   * @param {string} [options.drugName] - Drug name to set as top-level drug_name field.
   * @param {Object} [options.additionalInfo] - Additional event-specific data.
   * @returns {Object} The dispatched event payload.
   */
  const track = useCallback(
    (eventName, options = {}) => {
      const {
        featureGroup,
        feature,
        pageKey,
        contentType,
        contentKey,
        layout,
        potentialSaving,
        assistanceProgram,
        drugName,
        productSource = "frontend", // Allow override, default to "frontend"
        transport = "client", // "backend" relays via the API (billable events only)
        additionalInfo = {}
      } = options;

      const referralUri = location.state?.from || location.state?.path || null;

      const event = {
        event_name: eventName,
        event_uuid: generateEventUuid(),
        event_ts: new Date().toISOString(),
        session_id: getSessionId(),
        patient_id: patientId ? parseInt(patientId, 10) : null,
        product: PRODUCT,
        product_source: productSource,
        site,
        account: site || null,
        environment: getEnvironmentType() || null,
        page_uri: window.location.pathname,
        referral_page_uri: referralUri,
        user_id: user?.id || null
      };

      if (pageKey) event.page_key = pageKey;
      if (featureGroup) event.feature_group = featureGroup;
      if (feature) event.feature = feature;
      if (contentType) event.content_type = contentType;
      if (contentKey) event.content_key = contentKey;
      if (layout) event.layout = layout;

      if (eventName === EventNames.PAGE_IMPRESSION) {
        event.device_type = getDeviceType();
      }

      // Add AP fields if provided
      if (potentialSaving || assistanceProgram) {
        Object.assign(event, extractApFields(potentialSaving, assistanceProgram));
      }

      // drugName option sets drug_name as a top-level field (overrides AP-derived value if any)
      if (drugName) event.drug_name = drugName;

      if (Object.keys(additionalInfo).length > 0) {
        event.additional_info = additionalInfo;
      }

      if (transport === "backend") {
        relayEventToBackend(event);
      } else {
        trackPendoEvent(dispatch, eventName, event);
        trackMixpanelEvent(eventName, event);
      }
      return event;
    },
    [dispatch, user, site, patientId, location.state]
  );

  /**
   * Track once by key - prevents duplicate events.
   *
   * @param {string} key - Unique key for deduplication.
   * @param {Function} callback - Callback to execute if not already tracked.
   */
  const trackOnce = useCallback((key, callback) => {
    if (!trackedRef.current[key]) {
      trackedRef.current[key] = true;
      callback();
    }
  }, []);

  /**
   * Reset tracking state.
   *
   * @param {string} [key] - Specific key to reset, or all if not provided.
   */
  const resetTracking = useCallback((key) => {
    if (key) {
      delete trackedRef.current[key];
    } else {
      trackedRef.current = {};
    }
  }, []);

  return {
    track,
    trackOnce,
    resetTracking,
    // Expose constants for convenience
    EventNames,
    ContentTypes,
    LayoutTypes,
    FeatureGroups,
    PageKeys,
    EEContentKeys,
    SelfEnrollContentKeys,
    BulkActionContentKeys,
    InlineEditContentKeys,
    InlineEditStatus,
    RediContentKeys,
    RediFeatures
  };
};

export default useStandardizedTracking;
